import { Ohje } from "./Ohje";
import { useLataus, LatausTila } from "./Lataus";
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { naytaPvm } from "./pvm";
import { nayttonimi } from "./nayttonimi";
import {
  api, ApiError,
  type Kayttaja, type Tarjous, type TarjousDetalji, type TarjousTila,
  type Osaamisluokka, type Materiaali, type Sertifikaatti, type Asiakas,
  type TarjousYhteenveto,
} from "./api";
import { haeBrandi, type Brandi } from "./brandi";
import { TulosteLeima } from "./Tuloste";
import { t, tDok, virheTeksti, type KaannosAvain } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Tarjouslaskenta — vaihe 1: rakenne.

   Määrät (erät) → tunnit, muut kustannukset, kate ja riski. Laskenta
   (sivukulukerroin, TES-lisät) tulee vaiheessa 2 talon omilla luvuilla
   — niitä ei arvata. Tämä näkymä näyttää sen mikä on laskettavissa
   ilman kertoimia.
   ═══════════════════════════════════════════════════════════ */

/* Nimet avaimina: moduulin tasolla t() jäädyttäisi kielen lataushetkeen. */
const TILAT: { arvo: TarjousTila; avain: KaannosAvain }[] = [
  { arvo: "luonnos",    avain: "tar.tila_luonnos" },
  { arvo: "annettu",    avain: "tar.tila_annettu" },
  { arvo: "hyvaksytty", avain: "tar.tila_hyvaksytty" },
  { arvo: "havitty",    avain: "tar.tila_havitty" },
  { arvo: "peruttu",    avain: "tar.tila_peruttu" },
];
const tilaNimi = (tila: string): string => { const x = TILAT.find((y) => y.arvo === tila); return x ? t(x.avain) : tila; };

const KULULAJIT = [
  "materiaali", "kalusto", "ndt", "koeponnistus", "telineet",
  "dokumentointi", "vuokratyo", "paivaraha", "majoitus", "matkat", "muu",
];

const eur = (n: number) => (n ?? 0).toLocaleString("fi-FI", { maximumFractionDigits: 0 }) + " €";
const tun = (n: number) => (n ?? 0).toLocaleString("fi-FI", { maximumFractionDigits: 1 }) + " h";
const kerroin = (n: number) => (n ?? 1).toLocaleString("fi-FI", { maximumFractionDigits: 3 });

/* Elävä tulospaneeli — näe tarjoushinta ja €/laskutettava tunti heti,
   editoidessa. Sticky ylhäällä. (Opittu Aalto X -laskurista.) */
function LiveTulos({ y }: { y: TarjousYhteenveto }) {
  const perTunti = y.tunnit > 0 ? y.hinta / y.tunnit : 0;
  const beTunti = y.tunnit > 0 ? y.kokonaiskustannus / y.tunnit : 0;
  const kateEur = y.hinta - y.kokonaiskustannus;
  const kerr = y.kokonaiskustannus > 0 ? y.hinta / y.kokonaiskustannus : 0;
  const e2 = (n: number) => n.toLocaleString("fi-FI", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  const osat: [string, number, string][] = [
    [t("tar.osa_henkilosto"), y.henkilosto_kustannus - y.tes_lisat, "var(--korostus, #b00021)"],
    [t("tar.osa_tes"), y.tes_lisat, "#d99000"],
    [t("tar.osa_matkat"), y.matkat + y.paivaraha, "#6b8794"],
    [t("tar.osa_materiaali"), y.materiaali_kustannus, "#45a939"],
    [t("tar.osa_muut"), y.muut_kustannukset, "#9a9a90"],
  ];
  const kok = Math.max(y.kokonaiskustannus, 1);

  const kortti: CSSProperties = {
    position: "sticky", top: 0, zIndex: 5, margin: "0 0 18px",
    background: "#17171a", color: "#f0efe9", border: "1px solid #2e2e30", padding: "16px 18px",
  };
  const tiles: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 1, background: "#2e2e30", border: "1px solid #2e2e30", marginTop: 14 };
  const tile: CSSProperties = { background: "#17171a", padding: "9px 11px" };
  const tileB: CSSProperties = { fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 16, lineHeight: 1 };
  const tileS: CSSProperties = { color: "#9a998f", fontSize: 11, marginTop: 4, letterSpacing: ".02em" };

  return (
    <div style={kortti}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: "#9a998f" }}>{t("tar.ulosmyynti")}</div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: 6 }}>
        <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-.02em", color: "var(--korostus, #e0703a)", fontVariantNumeric: "tabular-nums" }}>
          {e2(perTunti)}
        </div>
        <div style={{ color: "#c9c8bf", fontSize: 13 }}>{t("tar.per_tunti")}</div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{eur(y.hinta)}</div>
          <div style={{ color: "#9a998f", fontSize: 11 }}>{t("tar.tarjoushinta_pieni")}</div>
        </div>
      </div>
      <div style={{ color: "#9a998f", fontSize: 12, marginTop: 4 }}>
        {tun(y.tunnit)} · {t("tar.eria_n", { n: y.eria })} · {t("tar.sivukulukerroin_arvo", { k: kerroin(y.sivukulukerroin) })}
      </div>

      <div style={tiles}>
        <div style={tile}><div style={tileB}>{eur(y.kokonaiskustannus)}</div><div style={tileS}>{t("tar.omakustannus")}</div></div>
        <div style={tile}><div style={{ ...tileB, color: "#8fd07a" }}>{eur(kateEur)}</div><div style={tileS}>{t("tar.kate_pros", { p: y.kate_pros })}</div></div>
        <div style={tile}><div style={tileB}>{kerroin(kerr)}×</div><div style={tileS}>{t("tar.kerroin")}</div></div>
        <div style={tile}><div style={tileB}>{e2(beTunti)}</div><div style={tileS}>{t("tar.break_even")}</div></div>
      </div>

      {/* Kustannusrakenne — mistä hinta koostuu */}
      <div style={{ display: "flex", height: 8, marginTop: 12, overflow: "hidden", background: "#2e2e30" }}>
        {osat.filter(([, v]) => v > 0).map(([n, v, c]) => (
          <div key={n} title={`${n}: ${eur(v)}`} style={{ width: `${(100 * v) / kok}%`, background: c }} />
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 7, fontSize: 10.5, color: "#9a998f" }}>
        {osat.filter(([, v]) => v > 0).map(([n, v, c]) => (
          <span key={n}><i style={{ display: "inline-block", width: 8, height: 8, background: c, marginRight: 4, verticalAlign: "-1px" }} />{n} {eur(v)}</span>
        ))}
      </div>
    </div>
  );
}

/* Asiakkaalle ojennettava tarjousdokumentti. EI sisäistä kustannus-
   erittelyä (henkilöstökustannus, kate) — vain laajuus ja hinta.
   Tulostuu selaimesta (Ctrl+P → paperi tai PDF); ei uutta riippuvuutta. */
function TarjousTuloste({ d, onSulje }: { d: TarjousDetalji; onSulje: () => void }) {
  const [brandi, setBrandi] = useState<Brandi | null>(null);
  const [sertit, setSertit] = useState<Sertifikaatti[]>([]);
  useEffect(() => { void haeBrandi().then(setBrandi); }, []);
  useEffect(() => { api.sertifikaatit().then(setSertit).catch(() => setSertit([])); }, []);
  const { tarjous: tj, erat, kustannukset, yhteenveto: y } = d;
  const pvm = new Date().toLocaleDateString("fi-FI");

  return (
    <div className="tuloste-overlay">
      <div className="tuloste-tyokalut">
        <button className="nappi nappi--paa" onClick={() => window.print()}>{t("tar.tulosta_pdf")}</button>
        <button className="nappi nappi--kevyt" onClick={onSulje}>{t("hl.sulje")}</button>
      </div>
      <article className="tuloste">
        <header className="tuloste__paa">
          <TulosteLeima brandi={brandi} />
          <div className="tuloste__otsikko">
            <h1>{tDok("tar.d_otsikko")}</h1>
            <div className="tuloste__meta">
              <div className="mono"><b>{tj.tunnus}</b></div>
              <div>{pvm}</div>
            </div>
          </div>
        </header>
        <div className="tuloste__osapuolet-grid">
          <div className="tuloste__osapuoli">
            <div className="tuloste__osapuoli-otsikko">{tDok("tar.d_lahettaja")}</div>
            <div className="tuloste__yritys">{brandi?.nimi ?? "—"}</div>
            {brandi?.ytunnus && <div>{tDok("tar.d_ytunnus", { y: brandi.ytunnus })}</div>}
            {brandi?.osoite && <div>{brandi.osoite}</div>}
            {brandi?.puhelin && <div>{brandi.puhelin}</div>}
            {brandi?.verkkosivu && <div>{brandi.verkkosivu}</div>}
            {tj.myyja && (
              <div className="tuloste__kontakti">
                {tDok("tar.d_yhteyshenkilo", { nimi: tj.myyja })}
                {brandi?.tukiosoite ? ` · ${brandi.tukiosoite}` : ""}
              </div>
            )}
          </div>
          <div className="tuloste__osapuoli">
            <div className="tuloste__osapuoli-otsikko">{tDok("tar.d_vastaanottaja")}</div>
            <div className="tuloste__yritys">{tj.asiakas_nimi ?? tj.tilaaja ?? "—"}</div>
            {tj.asiakas_ytunnus && <div>{tDok("tar.d_ytunnus", { y: tj.asiakas_ytunnus })}</div>}
            {tj.asiakas_osoite && <div>{tj.asiakas_osoite}</div>}
            {tj.asiakas_yhteyshenkilo && (
              <div className="tuloste__kontakti">{tDok("tar.d_tilaaja", { nimi: tj.asiakas_yhteyshenkilo })}</div>
            )}
            {tj.asiakas_puhelin && <div>{tj.asiakas_puhelin}</div>}
            {tj.asiakas_sahkoposti && <div>{tj.asiakas_sahkoposti}</div>}
          </div>
        </div>

        <h2 className="tuloste__aihe">{tj.nimi}</h2>
        {tj.alkaa && (
          <p className="tuloste__toteutus">{tDok("tar.d_toteutus", { alku: naytaPvm(tj.alkaa) ?? "", loppu: tj.paattyy ?? "" })}</p>
        )}

        <h3>{tDok("tar.d_sisalto")}</h3>
        <table className="tuloste__taulu">
          <thead>
            <tr><th>{tDok("tar.kuvaus")}</th><th className="oikea">{tDok("tar.maara")}</th><th className="oikea">{tDok("tar.d_tyotunnit")}</th></tr>
          </thead>
          <tbody>
            {erat.map((e) => (
              <tr key={e.id}>
                <td>{e.kuvaus}{e.dn ? ` · DN${e.dn}` : ""}{e.materiaali ? ` · ${e.materiaali}` : ""}</td>
                <td className="oikea mono">{e.maara} {e.yksikko}</td>
                <td className="oikea mono">{e.normi_h != null ? tun(e.maara * e.normi_h) : "—"}</td>
              </tr>
            ))}
            {erat.length === 0 && <tr><td colSpan={3} className="dim">—</td></tr>}
          </tbody>
        </table>

        {kustannukset.length > 0 && (
          <>
            <h3>{tDok("tar.d_sisaltyy")}</h3>
            <ul className="tuloste__sisaltyy">
              {kustannukset.map((k) => <li key={k.id}>{k.kuvaus}</li>)}
            </ul>
          </>
        )}

        <table className="tuloste__yhteenveto">
          <tbody>
            <tr className="tuloste__hinta">
              <th>{tDok("tar.tarjoushinta")}</th><td className="mono">{eur(y.hinta)}</td>
            </tr>
            <tr><td colSpan={2} className="tuloste__alv">{tDok("tar.d_alv")}</td></tr>
          </tbody>
        </table>

        {tj.sakkoehto && <p className="tuloste__ehto"><b>{tDok("tar.d_sakko")}</b> {tj.sakkoehto}</p>}
        {tj.huomiot && <p className="tuloste__ehto">{tj.huomiot}</p>}
        {sertit.length > 0 && (
          <p className="tuloste__sertit">
            {tDok("tar.d_sertit", { lista: sertit.map((s) => s.nimi + (s.tunnus ? ` (${s.tunnus})` : "")).join(" · ") })}
          </p>
        )}
        <p className="tuloste__alaviite">
          {tDok("tar.d_alaviite")}
        </p>
      </article>
    </div>
  );
}

export default function Tarjoukset({ kayttaja }: { kayttaja: Kayttaja }) {
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));

  const [lista, setLista] = useState<Tarjous[] | null>(null);
  const [valittu, setValittu] = useState<number | null>(null);
  const [uusiAuki, setUusiAuki] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  const lataa = useCallback(async () => {
    try { setLista(await api.tarjoukset()); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setLista([]); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  const tunnus = useMemo(() => {
    const l = lista ?? [];
    const avoin = l.filter((t) => t.tila === "luonnos" || t.tila === "annettu");
    const voitto = l.filter((t) => t.tila === "hyvaksytty").length;
    const havio = l.filter((t) => t.tila === "havitty").length;
    return {
      avoinna: avoin.length,
      kanta: avoin.reduce((s, t) => s + (t.hinta ?? 0), 0),
      osuma: voitto + havio > 0 ? Math.round((100 * voitto) / (voitto + havio)) : null,
      voitetut: voitto,
    };
  }, [lista]);

  if (valittu !== null) {
    return <TarjousNakyma id={valittu} saaMuokata={saaMuokata}
      takaisin={() => { setValittu(null); void lataa(); }} />;
  }

  return (
    <>
      <header className="head">
        <div className="head__rivi">
          <h1>{t("valikko.tarjoukset")}</h1>
          {saaMuokata && (
            <button className="nappi nappi--paa" onClick={() => setUusiAuki((v) => !v)}>
              {uusiAuki ? t("hl.peruuta") : t("tar.uusi")}
            </button>
          )}
        </div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/tarjoukset*: vaadiRooli(paakayttaja, suunnittelija) + rajaamaton + moduuli tarjous; laskenta f_tarjous_yhteenveto (045). */}
      <Ohje
        kuka={<>{t("tar.ohje.kuka")}</>}
        luvut={<>{t("tar.ohje.luvut")}</>} />

      {lista && lista.length > 0 && (
        <div className="tunnusluvut">
          <div className="tunnusluku"><span className="tunnusluku__otsikko">{t("tar.avoinna")}</span><b>{tunnus.avoinna}</b></div>
          <div className="tunnusluku"><span className="tunnusluku__otsikko">{t("tar.kanta")}</span><b>{eur(tunnus.kanta)}</b></div>
          <div className="tunnusluku"><span className="tunnusluku__otsikko">{t("tar.osuma")}</span><b>{tunnus.osuma == null ? "—" : tunnus.osuma + " %"}</b></div>
          <div className="tunnusluku tunnusluku--korostus"><span className="tunnusluku__otsikko">{t("tar.voitetut")}</span><b>{tunnus.voitetut}</b></div>
        </div>
      )}

    <section className="panel">
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      {uusiAuki && <UusiTarjous valmis={async (id) => { setUusiAuki(false); await lataa(); setValittu(id); }} />}

      {lista === null && <p className="dim">{t("yleinen.ladataan")}</p>}
      {lista?.length === 0 && !uusiAuki && (
        <p className="dim">{t("tar.tyhja")}</p>
      )}

      {lista && lista.length > 0 && (
        <ul className="kalustolista">
          {lista.map((tj) => (
            <li key={tj.id} className="kalusto">
              <button className="kalusto__paa" onClick={() => setValittu(tj.id)}>
                <span className="lyh">{tj.tunnus}</span>
                <span className="kalusto__nimi">{tj.nimi}</span>
                <span className="dim mono">{tj.tilaaja ?? "—"}</span>
                <span className={`ttila ttila--${tj.tila}`}>{tilaNimi(tj.tila)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
    </>
  );
}

/* ── uusi tarjous ───────────────────────────────────────── */

function UusiTarjous({ valmis }: { valmis: (id: number) => Promise<void> }) {
  const [tunnus, setTunnus] = useState("");
  const [nimi, setNimi] = useState("");
  const [asiakkaat, setAsiakkaat] = useState<Asiakas[]>([]);
  const [asiakasId, setAsiakasId] = useState<number | "" | "uusi">("");
  const [uusiAsiakas, setUusiAsiakas] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  useEffect(() => { api.asiakkaat().then(setAsiakkaat).catch(() => setAsiakkaat([])); }, []);

  async function tallenna(e: React.FormEvent) {
    e.preventDefault();
    if (!tunnus.trim() || !nimi.trim()) { setVirhe(t("tar.v_tunnus_nimi")); return; }
    setVirhe(null); setTallennetaan(true);
    try {
      const r = await api.luoTarjous({
        tunnus: tunnus.trim(), nimi: nimi.trim(),
        ...(asiakasId === "uusi" && uusiAsiakas.trim()
          ? { asiakas_nimi: uusiAsiakas.trim() }
          : asiakasId !== "" && asiakasId !== "uusi" ? { asiakas_id: asiakasId } : {}),
      });
      await valmis(r.id);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <form className="lomake panel panel--korostus" onSubmit={(e) => void tallenna(e)}>
      <div className="allokointi">
        <label>{t("iv.tunnus")}<input value={tunnus} onChange={(e) => setTunnus(e.target.value)} size={10} autoFocus /></label>
        <label>{t("iv.nimi")}<input value={nimi} onChange={(e) => setNimi(e.target.value)} size={22} /></label>
        <label>{t("tar.asiakas")}
          <select value={asiakasId}
                  onChange={(e) => setAsiakasId(e.target.value === "" ? "" : e.target.value === "uusi" ? "uusi" : Number(e.target.value))}>
            <option value="">{t("qr.ei_valittu")}</option>
            {asiakkaat.map((a) => <option key={a.id} value={a.id}>{a.nimi}</option>)}
            <option value="uusi">{t("tar.uusi_asiakas")}</option>
          </select>
        </label>
        {asiakasId === "uusi" && (
          <label>{t("tar.uuden_asiakkaan_nimi")}
            <input value={uusiAsiakas} onChange={(e) => setUusiAsiakas(e.target.value)} size={18} />
          </label>
        )}
        <button className="nappi nappi--paa" type="submit" disabled={tallennetaan}>
          {tallennetaan ? t("pu.tallennetaan") : t("tar.luo")}
        </button>
      </div>
      {virhe && <p className="huom" role="alert">{virhe}</p>}
    </form>
  );
}

/* ── yksi tarjous ───────────────────────────────────────── */

function TarjousNakyma({
  id, saaMuokata, takaisin,
}: { id: number; saaMuokata: boolean; takaisin: () => void }) {
  const [d, setD] = useState<TarjousDetalji | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  // erä-lomake
  const [eKuvaus, setEKuvaus] = useState("");
  const [eMaara, setEMaara] = useState("");
  const [eYksikko, setEYksikko] = useState("kpl");
  const [eDn, setEDn] = useState("");
  const [eNormi, setENormi] = useState("");
  const [eOsaaminen, setEOsaaminen] = useState<number | "">("");
  const [osaamisluokat, setOsaamisluokat] = useState<Osaamisluokka[]>([]);
  const [tesit, setTesit] = useState<{ id: number; nimi: string }[]>([]);
  const [tulosta, setTulosta] = useState(false);
  // kustannus-lomake
  const [kLaji, setKLaji] = useState("materiaali");
  const [kKuvaus, setKKuvaus] = useState("");
  const [kSumma, setKSumma] = useState("");
  // materiaali (BOM) -lomake
  const [katalogi, setKatalogi] = useState<Materiaali[]>([]);
  const [mValinta, setMValinta] = useState<number | "">("");
  const [mKuvaus, setMKuvaus] = useState("");
  const [mMaara, setMMaara] = useState("");
  const [mYksikko, setMYksikko] = useState("kpl");
  const [mHinta, setMHinta] = useState("");

  const lataa = useCallback(async () => {
    try { setD(await api.tarjous(id)); } catch { setD(null); }
  }, [id]);
  useEffect(() => { void lataa(); }, [lataa]);
  const klOsaamis = useLataus();
  useEffect(() => { klOsaamis.seuraa(api.osaamisluokat().then(setOsaamisluokat)); }, [klOsaamis.kerta]);
  useEffect(() => { api.tesitLista().then(setTesit).catch(() => setTesit([])); }, []);
  useEffect(() => { api.materiaalit().then(setKatalogi).catch(() => setKatalogi([])); }, []);

  // Katalogivalinta täyttää kuvauksen, yksikön ja hinnan.
  function valitseMateriaali(idStr: string) {
    const mid = idStr ? Number(idStr) : "";
    setMValinta(mid);
    const m = katalogi.find((x) => x.id === mid);
    if (m) {
      setMKuvaus(m.nimi); setMYksikko(m.yksikko);
      setMHinta(m.sisaostohinta != null ? String(m.sisaostohinta) : "");
    }
  }
  async function lisaaMateriaali() {
    const maara = Number(mMaara.replace(",", "."));
    if (!(maara >= 0)) { setIlmoitus(t("tar.v_maara")); return; }
    if (mValinta === "" && !mKuvaus.trim()) { setIlmoitus(t("tar.v_nimike")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.lisaaTarjousMateriaali(id, {
        materiaali_id: mValinta === "" ? null : mValinta,
        kuvaus: mKuvaus.trim() || undefined,
        maara, yksikko: mYksikko.trim() || "kpl",
        ...(mHinta.trim() !== "" && { yksikkohinta: Number(mHinta.replace(",", ".")) }),
      });
      setMValinta(""); setMKuvaus(""); setMMaara(""); setMYksikko("kpl"); setMHinta("");
      await lataa();
    } catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallennetaan(false); }
  }

  async function paivita(muutos: Record<string, unknown>) {
    setTallennetaan(true);
    try { await api.paivitaTarjous(id, muutos); await lataa(); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallennetaan(false); }
  }

  async function lisaaEra() {
    const m = Number(eMaara.replace(",", "."));
    if (!eKuvaus.trim()) { setIlmoitus(t("tar.v_era_kuvaus")); return; }
    if (!(m >= 0)) { setIlmoitus(t("tar.v_maara_luku")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.lisaaEra(id, {
        kuvaus: eKuvaus.trim(), maara: m, yksikko: eYksikko.trim() || "kpl",
        ...(eDn && { dn: Number(eDn) }),
        ...(eNormi && { normi_h: Number(eNormi.replace(",", ".")) }),
        osaamisluokka_id: eOsaaminen === "" ? null : eOsaaminen,
      });
      setEKuvaus(""); setEMaara(""); setEDn(""); setENormi(""); setEOsaaminen("");
      await lataa();
    } catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallennetaan(false); }
  }

  async function lisaaKustannus() {
    const s = Number(kSumma.replace(",", "."));
    if (!kKuvaus.trim()) { setIlmoitus(t("tar.v_kulu_kuvaus")); return; }
    if (!(s >= 0)) { setIlmoitus(t("tar.v_summa_luku")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.lisaaKustannus(id, { laji: kLaji, kuvaus: kKuvaus.trim(), summa: s });
      setKKuvaus(""); setKSumma("");
      await lataa();
    } catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ltar.tallennus_virhe")); }
    finally { setTallennetaan(false); }
  }

  if (d === null) return <p className="dim">{t("yleinen.ladataan")}</p>;
  const { tarjous: tj, erat, kustannukset, materiaalit, yhteenveto: y } = d;

  return (
    <section className="panel">
      <LatausTila l={klOsaamis} nimi="lataus.osaamisluokat" />
      <div className="panel__otsikko">
        <div>
          <button className="nappi nappi--kevyt" onClick={takaisin}>{t("tar.takaisin")}</button>
          <h2 style={{ display: "inline", marginLeft: 12 }}>
            <span className="lyh">{tj.tunnus}</span> {tj.nimi}
          </h2>
        </div>
        {saaMuokata && (
          <label className="dim">{t("pu.tila")}{" "}
            <select value={tj.tila} disabled={tallennetaan}
                    onChange={(e) => void paivita({ tila: e.target.value })}>
              {TILAT.map((x) => <option key={x.arvo} value={x.arvo}>{t(x.avain)}</option>)}
            </select>
          </label>
        )}
        <button className="nappi nappi--kevyt" onClick={() => setTulosta(true)}>{t("tar.tulosta")}</button>
      </div>
      {tulosta && <TarjousTuloste d={d} onSulje={() => setTulosta(false)} />}
      <p className="dim">{t("tar.d_tilaaja", { nimi: tj.tilaaja ?? "—" })}</p>

      {/* Elävä tulos — hinta ja €/tunti näkyvät koko ajan editoidessa. */}
      <LiveTulos y={y} />

      {/* ── Erät ── */}
      <h3>{t("tar.erat")}</h3>
      {erat.length === 0 && <p className="dim">{t("tar.ei_eria")}</p>}
      {erat.length > 0 && (
        <ul className="kohdelista">
          {erat.map((e) => (
            <li key={e.id}>
              <span>
                <strong>{e.maara}</strong> {e.yksikko} · {e.kuvaus}
                {e.dn && <span className="mono dim"> DN{e.dn}</span>}
                {e.arvio && <span className="dim"> {t("tar.arvio")}</span>}
                {e.osaamisluokka_nimi
                  ? <span className="dim"> · {e.osaamisluokka_nimi}</span>
                  : <span className="tarjous__vaje"> · {t("tar.aseta_osaamisluokka")}</span>}
              </span>
              <span className="mono">
                {e.normi_h != null ? tun(e.maara * e.normi_h) : <span className="dim">{t("tar.normi_puuttuu")}</span>}
              </span>
              {saaMuokata && (
                <button className="nappi nappi--kevyt" disabled={tallennetaan}
                        onClick={() => void api.poistaEra(e.id).then(lataa)}>{t("liite.poista")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {saaMuokata && (
        <div className="allokointi">
          <label>{t("tar.kuvaus")}<input value={eKuvaus} onChange={(e) => setEKuvaus(e.target.value)} size={18} /></label>
          <label>{t("tar.maara")}<input type="text" size={5} value={eMaara} onChange={(e) => setEMaara(e.target.value)} /></label>
          <label>{t("iv.yksikko")}<input size={5} value={eYksikko} onChange={(e) => setEYksikko(e.target.value)} /></label>
          <label>DN<input type="number" size={4} value={eDn}
                 onChange={(e) => setEDn(e.target.value)}
                 onBlur={async () => {
                   if (eDn && !eNormi.trim()) {
                     try {
                       const r = await api.hitsausnormiEhdotus(Number(eDn));
                       if (r.normi_h != null) setENormi(String(r.normi_h));
                     } catch { /* ehdotus on avustava, ei pakollinen */ }
                   }
                 }} /></label>
          <label>{t("tar.normi_h_yks")}<input type="text" size={5} value={eNormi} onChange={(e) => setENormi(e.target.value)}
                 placeholder={t("tar.taulukosta")} /></label>
          <label>{t("tar.osaaminen")}
            <select value={eOsaaminen}
                    onChange={(e) => setEOsaaminen(e.target.value ? Number(e.target.value) : "")}>
              <option value="">—</option>
              {osaamisluokat.map((o) => <option key={o.id} value={o.id}>{o.nimi}</option>)}
            </select>
          </label>
          <button className="nappi nappi--paa" disabled={tallennetaan} onClick={() => void lisaaEra()}>{t("tar.lisaa_era")}</button>
        </div>
      )}

      {/* ── Materiaalit (BOM) ── */}
      <h3>{t("valikko.materiaalit")}</h3>
      {materiaalit.length === 0 && <p className="dim">{t("tar.ei_materiaaleja")}</p>}
      {materiaalit.length > 0 && (
        <ul className="kohdelista">
          {materiaalit.map((m) => (
            <li key={m.id}>
              <span><strong>{m.maara}</strong> {m.yksikko} · {m.kuvaus}</span>
              <span className="mono">
                {eur(m.yksikkohinta)}/{m.yksikko} · <strong>{eur(m.maara * m.yksikkohinta)}</strong>
              </span>
              {saaMuokata && (
                <button className="nappi nappi--kevyt" disabled={tallennetaan}
                        onClick={() => void api.poistaTarjousMateriaali(m.id).then(lataa)}>{t("liite.poista")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {saaMuokata && (
        <div className="allokointi">
          <label>{t("tar.nimike")}
            <select value={mValinta} onChange={(e) => valitseMateriaali(e.target.value)}>
              <option value="">{t("tar.vapaa_rivi")}</option>
              {katalogi.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nimi}{m.dn ? ` DN${m.dn}` : ""}{m.paineluokka ? ` ${m.paineluokka}` : ""}{m.sisaostohinta != null ? ` · ${m.sisaostohinta} €/${m.yksikko}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label>{t("tar.kuvaus")}<input value={mKuvaus} onChange={(e) => setMKuvaus(e.target.value)} size={16} /></label>
          <label>{t("tar.maara")}<input type="text" size={5} value={mMaara} onChange={(e) => setMMaara(e.target.value)} /></label>
          <label>{t("iv.yksikko")}<input size={4} value={mYksikko} onChange={(e) => setMYksikko(e.target.value)} /></label>
          <label>{t("mat.eur_yks")}<input type="text" size={6} value={mHinta} onChange={(e) => setMHinta(e.target.value)}
                 placeholder={t("tar.katalogista")} /></label>
          <button className="nappi nappi--paa" disabled={tallennetaan} onClick={() => void lisaaMateriaali()}>{t("yri.lisaa")}</button>
        </div>
      )}
      <p className="ikkuna__vihje">
        {t("tar.yksikkohinta_vihje")}
      </p>

      {/* ── Muut kustannukset ── */}
      <h3>{t("tar.muut_kustannukset")}</h3>
      {kustannukset.length === 0 && <p className="dim">{t("tar.ei_kustannuksia")}</p>}
      {kustannukset.length > 0 && (
        <ul className="kohdelista">
          {kustannukset.map((k) => (
            <li key={k.id}>
              <span><span className="dim">{nayttonimi("kustannus_laji", k.laji)}</span> · {k.kuvaus}</span>
              <span className="mono">{eur(k.summa)}</span>
              {saaMuokata && (
                <button className="nappi nappi--kevyt" disabled={tallennetaan}
                        onClick={() => void api.poistaKustannus(k.id).then(lataa)}>{t("liite.poista")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {saaMuokata && (
        <div className="allokointi">
          <label>{t("qr.laji")}
            <select value={kLaji} onChange={(e) => setKLaji(e.target.value)}>
              {KULULAJIT.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label>{t("tar.kuvaus")}<input value={kKuvaus} onChange={(e) => setKKuvaus(e.target.value)} size={18} /></label>
          <label>{t("tar.summa")}<input type="text" size={8} value={kSumma} onChange={(e) => setKSumma(e.target.value)} /></label>
          <button className="nappi nappi--paa" disabled={tallennetaan} onClick={() => void lisaaKustannus()}>{t("tar.lisaa_kulu")}</button>
        </div>
      )}

      {/* ── Vuorojako ja TES (TES-lisien laskentaan) ── */}
      {saaMuokata && tesit.length > 0 && (
        <>
          <h3>{t("tar.vuorojako")}</h3>
          <div className="allokointi">
            <label>{t("tar.tes")}
              <select value={tj.tes_id ?? ""}
                      onChange={(e) => void paivita({ tes_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{t("qr.ei_valittu")}</option>
                {tesit.map((x) => <option key={x.id} value={x.id}>{x.nimi}</option>)}
              </select>
            </label>
            {([
              ["osuus_ilta", "tar.os_ilta"], ["osuus_yo", "tar.os_yo"],
              ["osuus_lauantai", "tar.os_la"], ["osuus_sunnuntai", "tar.os_su"],
              ["osuus_ylityo50", "tar.os_yt50"], ["osuus_ylityo100", "tar.os_yt100"],
            ] as const).map(([kentta, nimi]) => (
              <label key={kentta}>{t(nimi)}
                <input type="number" size={4} min={0} max={100} step={1}
                       defaultValue={tj[kentta]}
                       onBlur={(e) => void paivita({ [kentta]: Number(e.target.value) })} />
              </label>
            ))}
          </div>
          <p className="ikkuna__vihje">
            {t("tar.vuorojako_vihje")}
          </p>
        </>
      )}

      {/* ── Yhteenveto ── */}
      <h3>{t("tar.yhteenveto")}</h3>
      <table className="yhteenveto">
        <tbody>
          <tr><th>{t("tar.y_eria")}</th><td>{y.eria}</td></tr>
          <tr><th>{t("tar.y_tunnit")}</th><td>{tun(y.tunnit)}</td></tr>
          <tr>
            <th>{t("tar.y_henkilosto")}</th>
            <td>{eur(y.henkilosto_kustannus)}
              <span className="dim"> · {t("tar.y_henkilosto_kaava", { k: kerroin(y.sivukulukerroin) })}</span>
            </td>
          </tr>
          {y.tes_lisat > 0 && (
            <tr><th className="dim">{t("tar.y_josta_tes")}</th>
              <td className="dim">{eur(y.tes_lisat)} · {t("tar.y_vuorojaosta")}</td></tr>
          )}
          {y.matkat > 0 && <tr><th>{t("tar.y_matkat")}</th><td>{eur(y.matkat)}</td></tr>}
          {y.paivaraha > 0 && <tr><th>{t("tar.y_paivaraha")}</th><td>{eur(y.paivaraha)}</td></tr>}
          {y.materiaali_kustannus > 0 && <tr><th>{t("tar.osa_materiaali")}</th><td>{eur(y.materiaali_kustannus)}</td></tr>}
          <tr><th>{t("tar.muut_kustannukset")}</th><td>{eur(y.muut_kustannukset)}</td></tr>
          <tr><th>{t("tar.y_kokonais")}</th><td>{eur(y.kokonaiskustannus)}</td></tr>
          <tr>
            <th>{t("tar.y_kate")}</th>
            <td>{saaMuokata
              ? <input type="number" size={4} defaultValue={tj.kate_pros} min={0} step={0.5}
                       onBlur={(e) => void paivita({ kate_pros: Number(e.target.value) })} />
              : tj.kate_pros} %</td>
          </tr>
          <tr>
            <th>{t("tar.y_riski")}</th>
            <td>{saaMuokata
              ? <input type="number" size={4} defaultValue={tj.riski_pros} min={0} step={0.5}
                       onBlur={(e) => void paivita({ riski_pros: Number(e.target.value) })} />
              : tj.riski_pros} %</td>
          </tr>
          <tr className="yhteenveto__hinta">
            <th>{t("tar.tarjoushinta")}</th><td>{eur(y.hinta)}</td>
          </tr>
        </tbody>
      </table>
      <p className="ikkuna__vihje">
        {t("tar.hinta_vihje")}
      </p>

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
    </section>
  );
}
